import { useReducer, createContext, useContext, useCallback, useMemo, memo, useEffect, useRef, } from 'react';
import { createPortal } from 'react-dom';
import { FocusTrap } from '@mui/base/FocusTrap';
import { WIDGET_Z_INDEX } from 'root/src/client/v2/layout/fixtures/layout.fixtures';
import { twMerge } from 'tailwind-merge';
export const DEFAULT_MODAL_OPTIONS = {
    isCloseable: true,
    zIndex: WIDGET_Z_INDEX + 1,
    fullScreen: false,
};
const modalContext = createContext(null);
export const hideOverflow = () => {
    // override with important
    document.body.style.setProperty('overflow', 'hidden', 'important');
};
export const showOverflow = () => {
    document.body.style.setProperty('overflow', 'auto');
};
export const useDocumentOverflow = () => {
    return {
        hide: hideOverflow,
        show: showOverflow,
    };
};
const useKeydown = (key, callback) => {
    useEffect(() => {
        const handler = (e) => {
            if (e.key === key) {
                callback();
            }
        };
        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
        };
    }, [key, callback]);
};
/**
 * WARNING: This is an imperative modal context for programmatic control, please use with caution and only when necessary
 */
export let modalContextImperative = {};
const useModalContext = () => {
    const ctx = useContext(modalContext);
    if (!ctx) {
        throw new Error('No modal provider');
    }
    return ctx;
};
const useModalProvider = () => {
    const dialogRef = useRef(null);
    const overflow = useDocumentOverflow();
    const [modalState, dispatchModalState] = useReducer((state, action) => {
        return Object.assign(Object.assign({}, state), action);
    }, {
        modal: null,
        options: DEFAULT_MODAL_OPTIONS,
    });
    const show = useCallback((modal, options) => {
        dispatchModalState({
            modal,
            options: Object.assign(Object.assign({}, DEFAULT_MODAL_OPTIONS), options),
        });
        overflow.hide();
    }, []);
    const hide = useCallback(() => {
        dispatchModalState({
            modal: null,
            options: DEFAULT_MODAL_OPTIONS,
        });
        overflow.show();
    }, [modalState]);
    // hide comes from all sources, close just from intentional closes (x, backdrop)
    const close = useCallback(() => {
        var _a, _b;
        (_b = (_a = modalState.options) === null || _a === void 0 ? void 0 : _a.onClose) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, [modalState]);
    useKeydown('Escape', () => {
        if (modalState.options.isCloseable) {
            hide();
        }
    });
    const ctx = useMemo(() => {
        return {
            show,
            hide,
            close,
            modal: modalState.modal,
            options: modalState.options,
        };
    }, [modalState]);
    return {
        ctx,
        dialogRef,
    };
};
// TODO: add focus trap
export const ModalContextProvider = memo(({ children, className, }) => {
    var _a;
    const { ctx, dialogRef } = useModalProvider();
    const isModalVisible = Boolean(ctx.modal);
    // assign context to imperative variable
    modalContextImperative = ctx;
    return (<modalContext.Provider value={ctx}>
			<div className={twMerge(isModalVisible ? 'overflow-hidden' : 'overflow-auto', className)}>
				{children}
			</div>
			{ctx.modal && createPortal(<>
					<Overlay />
					<dialog ref={dialogRef} open={isModalVisible} className={twMerge('flex m-0 py-2 max-h-full-safe max-w-[100svw] overflow-auto bg-transparent fixed left-1/2 -translate-x-1/2 -translate-y-1/2 top-1/2', ((_a = ctx.options) === null || _a === void 0 ? void 0 : _a.fullScreen) && 'w-full h-full m-0 p-0')} style={{
                zIndex: ctx.options.zIndex,
            }}>
						<FocusTrap open>
							{ctx.modal}
						</FocusTrap>
					</dialog>
				</>, document.body)}
		</modalContext.Provider>);
});
const Overlay = memo(() => {
    var _a;
    const { hide, close, options } = useModalContext();
    const onOverlayClick = useCallback(() => {
        if (options.isCloseable) {
            close();
            hide();
        }
    }, [options.isCloseable]);
    return (<div onClick={onOverlayClick} className="flex backdrop-blur overflow-auto fixed top-0 left-0 w-full h-full-safe items-center justify-center" style={{
            zIndex: ((_a = options.zIndex) !== null && _a !== void 0 ? _a : DEFAULT_MODAL_OPTIONS.zIndex) - 1,
        }}/>);
});
export function useModal() {
    const ctx = useModalContext();
    const { show, hide, options, close, } = ctx;
    return {
        hide,
        show,
        close,
        options,
    };
}
